
import React, { useState, useRef, useEffect } from 'react';
import { processQuoteAssistantMessage } from '../services/geminiService';
import { QuoteState, LineItem } from '../types';
import { Sparkles, Send, X, Bot, Loader2, Wand2, Info, ChevronRight, Mic, MicOff, AlertCircle } from 'lucide-react';

interface QuoteChatAssistantProps {
  quote: QuoteState;
  onUpdateQuote: (newQuote: QuoteState) => void;
  onClose: () => void;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  isUpdating?: boolean;
}

export const QuoteChatAssistant: React.FC<QuoteChatAssistantProps> = ({ quote, onUpdateQuote, onClose }) => {
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', content: 'Hello! I am your Nexus Quote Sidekick. I can add items, set pricing, or fill in client info. How can I help today?' }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Speech Recognition Setup
  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onstart = () => setIsListening(true);
      recognitionRef.current.onend = () => setIsListening(false);
      
      recognitionRef.current.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setInput(prev => prev ? `${prev} ${transcript}` : transcript);
      };

      recognitionRef.current.onerror = (event: any) => {
        console.error("Speech Recognition Error", event.error);
        setIsListening(false);
        if (event.error === 'not-allowed') {
          setError("Microphone access denied.");
        }
      };
    }
  }, []);

  const toggleListening = () => {
    if (!recognitionRef.current) {
      alert("Voice commands are not supported in this browser.");
      return;
    }
    if (isListening) {
      recognitionRef.current.stop();
    } else {
      setError(null);
      recognitionRef.current.start();
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMsg: Message = { role: 'user', content: input };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    try {
      const result = await processQuoteAssistantMessage(input, quote);
      
      let nextQuote = { ...quote };
      let appliedChanges = false;

      if (result.actions && result.actions.length > 0) {
        appliedChanges = true;
        result.actions.forEach((action: any) => {
          if (action.name === 'add_line_items') {
            const newItems: LineItem[] = action.args.items.map((it: any) => ({
              ...it,
              id: crypto.randomUUID()
            }));
            nextQuote.items = [...nextQuote.items, ...newItems];
          } else if (action.name === 'update_client_info') {
            nextQuote.client = { ...nextQuote.client, ...action.args };
          } else if (action.name === 'configure_pricing') {
            nextQuote.details = { ...nextQuote.details, ...action.args };
          }
        });
        onUpdateQuote(nextQuote);
      }

      const assistantMsg: Message = { 
        role: 'assistant', 
        content: result.text,
        isUpdating: appliedChanges
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: "I encountered a communication error with the intelligence core." }]);
    } finally {
      setIsLoading(false);
    }
  };

  const suggestions = [
    "Add 300 tons of 60/70 Bitumen",
    "Set client to TanRoads",
    "Change currency to TZS",
    "Apply 18% VAT"
  ];

  return (
    <div className="w-[400px] bg-white border-l border-slate-200 flex flex-col h-full shadow-2xl animate-in slide-in-from-right duration-300">
      <header className="p-6 bg-indigo-600 text-white flex justify-between items-center shadow-lg">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-md">
            <Sparkles size={20} />
          </div>
          <div>
            <h3 className="font-black text-sm uppercase tracking-widest">Nexus Sidekick</h3>
            <p className="text-[10px] font-bold opacity-70">AI Support Active</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
          <X size={20} />
        </button>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
        {error && (
            <div className="bg-rose-50 border border-rose-100 p-3 rounded-xl flex items-center gap-2 text-[10px] font-black text-rose-600 uppercase tracking-widest animate-in fade-in">
                <AlertCircle size={14} /> {error}
                <button onClick={() => setError(null)} className="ml-auto p-1 hover:bg-rose-100 rounded-lg"><X size={12}/></button>
            </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl p-4 text-sm leading-relaxed ${
              msg.role === 'user' 
                ? 'bg-indigo-600 text-white shadow-md' 
                : 'bg-slate-50 border border-slate-100 text-slate-700 shadow-sm'
            }`}>
              {msg.role === 'assistant' && (
                <div className="flex items-center gap-2 mb-2 opacity-50">
                  <Bot size={14} />
                  <span className="text-[10px] font-black uppercase">Assistant</span>
                </div>
              )}
              {msg.content}
              {msg.isUpdating && (
                <div className="mt-3 flex items-center gap-2 text-[10px] font-black text-indigo-600 bg-indigo-50 px-2 py-1 rounded-lg w-fit">
                   <Wand2 size={12} /> QUOTE SYNCHRONIZED
                </div>
              )}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start animate-pulse">
            <div className="bg-slate-50 p-4 rounded-2xl flex items-center gap-3 text-slate-400">
              <Loader2 className="animate-spin" size={16} />
              <span className="text-xs font-bold uppercase tracking-tighter">Drafting intelligence...</span>
            </div>
          </div>
        )}
      </div>

      <div className="p-4 bg-slate-50/50 border-t border-slate-100">
        {!isLoading && messages.length < 5 && (
          <div className="mb-4">
             <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
                <Info size={12} /> Try asking:
             </p>
             <div className="flex flex-wrap gap-2">
                {suggestions.map(s => (
                  <button 
                    key={s}
                    onClick={() => setInput(s)}
                    className="text-[10px] font-bold text-indigo-600 bg-white border border-indigo-100 px-3 py-1.5 rounded-full hover:bg-indigo-50 transition-all flex items-center gap-1"
                  >
                    {s} <ChevronRight size={10} />
                  </button>
                ))}
             </div>
          </div>
        )}
        <div className="relative flex items-center gap-2">
          <div className="relative flex-1">
            <input 
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder={isListening ? "Listening for commands..." : "Tell me what to add..."}
              className={`w-full pl-4 pr-12 py-4 bg-white border rounded-2xl shadow-sm focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all text-sm ${isListening ? 'border-indigo-500 ring-4 ring-indigo-500/10 italic' : 'border-slate-200'}`}
            />
            <button 
              onClick={toggleListening}
              className={`absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-xl transition-all ${isListening ? 'text-rose-500 bg-rose-50 animate-pulse scale-110' : 'text-slate-400 hover:text-indigo-600 hover:bg-indigo-50'}`}
              title={isListening ? "Stop listening" : "Use voice commands"}
            >
              {isListening ? <MicOff size={18} /> : <Mic size={18} />}
            </button>
          </div>
          <button 
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className="w-12 h-12 bg-indigo-600 text-white rounded-2xl flex items-center justify-center hover:bg-indigo-700 disabled:opacity-30 transition-all shadow-xl shadow-indigo-100 active:scale-90 shrink-0"
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};
